import { useSyncExternalStore } from 'react'

// Tracks, for the current browser session (until a full page reload), which tables have had
// an external import running at any point. Once a sync has started we keep treating the
// table as "syncing" for row-adding purposes even after it completes: the freshly imported
// rows only appear once the user reloads, and adding rows to a table that is (about to be)
// synced isn't allowed. A hard refresh recreates this module state, by which point the table
// loads as synced anyway — so the row-adding controls stay hidden either way.
//
// Components read it through useSyncStartedThisSession, not hasSyncStartedThisSession: the React
// Compiler memoises a render-time call to a plain function on the call's arguments, so a component
// calling it would keep its first answer until the table id (or whatever else it is keyed on)
// changes. The hook re-renders the component when a table is marked.

const tablesSyncedThisSession = new Set<string>()
const listeners = new Set<() => void>()

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const markSyncStartedThisSession = (tableId: string): void => {
  if (!tableId || tablesSyncedThisSession.has(tableId)) return
  tablesSyncedThisSession.add(tableId)
  listeners.forEach((listener) => listener())
}

/** For event handlers, effects and other code that runs outside render. Components use the hook below. */
export const hasSyncStartedThisSession = (tableId: string): boolean => tablesSyncedThisSession.has(tableId)

/** Whether an import has started on `tableId` this session; re-renders when one starts. */
export const useSyncStartedThisSession = (tableId: string): boolean =>
  useSyncExternalStore(
    subscribe,
    () => tablesSyncedThisSession.has(tableId),
    () => false
  )
